import React, { useState, useEffect, useCallback } from 'react';
// import { useAuth } from '../context/AuthContext'; // Uncomment and use if AuthContext is set up
import FloorListComponent from '../components/FloorListComponent';
import SectionListComponent from '../components/SectionListComponent';
import TableListComponent from '../components/TableListComponent';
import type {
  Floor,
  Section,
  VenueTable, // Types
} from '../api/api';
import {
  getFloors,
  getSectionsByFloor,
  getTablesBySection,
} from '../api/api'; // Adjusted path assuming api.ts is in src/api

const ManageLayoutPage: React.FC = () => {
  // const { user } = useAuth(); // Example: Get user/vendor info from context
  // const vendorId = user?.id; // Assuming user object has id which is vendorId
  // For development, if AuthContext isn't ready, you might rely on API client's interceptor
  // or a hardcoded ID if necessary, but ensure to replace with real auth.

  const [floors, setFloors] = useState<Floor[]>([]);
  const [selectedFloor, setSelectedFloor] = useState<Floor | null>(null);
  const [sections, setSections] = useState<Section[]>([]);
  const [selectedSection, setSelectedSection] = useState<Section | null>(null);
  const [tables, setTables] = useState<VenueTable[]>([]);

  const [isLoadingFloors, setIsLoadingFloors] = useState(false);
  const [isLoadingSections, setIsLoadingSections] = useState(false);
  const [isLoadingTables, setIsLoadingTables] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => setError(null), []);

  // Fetch floors
  useEffect(() => {
    // Assuming vendorId is handled by the API client interceptor or not needed if API is vendor-scoped by default
    setIsLoadingFloors(true);
    clearError();
    getFloors()
      .then(data => {
        setFloors(Array.isArray(data) ? data : []);
        // Reset dependent states if needed, or let selection drive it
        setSelectedFloor(null);
        setSections([]);
        setSelectedSection(null);
        setTables([]);
      })
      .catch(err => {
        console.error('Failed to fetch floors:', err);
        setError(err.response?.data?.message || err.message || 'Failed to load floors.');
      })
      .finally(() => setIsLoadingFloors(false));
  }, [clearError]); // Add dependencies if vendorId were used here

  // Fetch sections when selectedFloor changes
  useEffect(() => {
    if (selectedFloor && selectedFloor.id) {
      setIsLoadingSections(true);
      clearError();
      getSectionsByFloor(selectedFloor.id as string) // Assuming id is string or number, cast if needed
        .then(data => {
          setSections(Array.isArray(data) ? data : []);
        })
        .catch(err => {
          console.error(`Failed to fetch sections for floor ${selectedFloor.name}:`, err);
          setError(err.response?.data?.message || err.message || `Failed to load sections for ${selectedFloor.name}.`);
        })
        .finally(() => setIsLoadingSections(false));
    } else {
      setSections([]); // Clear sections if no floor selected
    }
    setSelectedSection(null); // Always reset section selection when floor changes
    setTables([]); // And clear tables
  }, [selectedFloor, clearError]);

  // Fetch tables when selectedSection changes
  useEffect(() => {
    if (selectedSection && selectedSection.id) {
      setIsLoadingTables(true);
      clearError();
      getTablesBySection(selectedSection.id as string) // Assuming id is string or number
        .then(data => {
          setTables(Array.isArray(data) ? data : []);
        })
        .catch(err => {
          console.error(`Failed to fetch tables for section ${selectedSection.name}:`, err);
          setError(err.response?.data?.message || err.message || `Failed to load tables for ${selectedSection.name}.`);
        })
        .finally(() => setIsLoadingTables(false));
    } else {
      setTables([]); // Clear tables if no section selected
    }
  }, [selectedSection, clearError]);

  const handleSelectFloor = useCallback((floor: Floor | null) => {
    clearError();
    if (selectedFloor?.id === floor?.id) { // Deselect if clicking the same floor
        setSelectedFloor(null);
    } else {
        setSelectedFloor(floor);
    }
    // Dependent states (sections, selectedSection, tables) are reset by their respective useEffect or directly
    // setSelectedSection(null); // Done in sections useEffect
    // setSections([]); // Done in sections useEffect
    // setTables([]); // Done in sections useEffect
  }, [selectedFloor, clearError]);

  const handleSelectSection = useCallback((section: Section | null) => {
    clearError();
    if (selectedSection?.id === section?.id) { // Deselect if clicking the same section
        setSelectedSection(null);
    } else {
        setSelectedSection(section);
    }
    // Dependent state (tables) is reset by its useEffect
    // setTables([]); // Done in tables useEffect
  }, [selectedSection, clearError]);


  return (
    <div className="container mx-auto p-4 md:p-6">
      <h1 className="text-3xl font-bold mb-6 text-[var(--color-text-primary)]">Manage Venue Layout</h1>

      {error && (
        <div
          className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-6"
          role="alert"
        >
          <strong className="font-bold">Error: </strong>
          <span className="block sm:inline">{error}</span>
          <span
            className="absolute top-0 bottom-0 right-0 px-4 py-3"
            onClick={() => setError(null)} // Allow dismissing error
          >
            <svg className="fill-current h-6 w-6 text-red-500" role="button" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><title>Close</title><path d="M14.348 14.849a1.2 1.2 0 0 1-1.697 0L10 11.819l-2.651 3.029a1.2 1.2 0 1 1-1.697-1.697l2.758-3.15-2.759-3.152a1.2 1.2 0 1 1 1.697-1.697L10 8.183l2.651-3.031a1.2 1.2 0 1 1 1.697 1.697l-2.758 3.152 2.758 3.15a1.2 1.2 0 0 1 0 1.698z"/></svg>
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Column for Floors */}
        <div className="bg-[var(--color-background-surface)] p-4 rounded-lg shadow">
           <h2 className="text-xl font-semibold mb-3 text-[var(--color-text-secondary)]">Floors</h2>
          <FloorListComponent
            floors={floors}
            setFloors={setFloors}
            selectedFloor={selectedFloor}
            onSelectFloor={handleSelectFloor}
            isLoadingInitial={isLoadingFloors}
            setError={setError}
          />
        </div>

        {/* Column for Sections */}
        <div className="bg-[var(--color-background-surface)] p-4 rounded-lg shadow">
          <h2 className="text-xl font-semibold mb-3 text-[var(--color-text-secondary)]">Sections</h2>
          <SectionListComponent
            selectedFloor={selectedFloor}
            sections={sections}
            setSections={setSections}
            selectedSection={selectedSection}
            onSelectSection={handleSelectSection}
            isLoadingInitial={isLoadingSections}
            setError={setError}
          />
        </div>

        {/* Column for Tables */}
        <div className="bg-[var(--color-background-surface)] p-4 rounded-lg shadow">
          <h2 className="text-xl font-semibold mb-3 text-[var(--color-text-secondary)]">Tables</h2>
          <TableListComponent
            selectedSection={selectedSection}
            tables={tables}
            setTables={setTables}
            isLoadingInitial={isLoadingTables}
            setError={setError}
          />
        </div>
      </div>
    </div>
  );
};

export default ManageLayoutPage;
